// ============================================================================
// OrderResultsPanel — shared "results & fulfilment" surface mounted under
// each Lab order, Imaging order, and Prescription card on the encounter
// editor.
//
// Pulls ClinicalDocuments owned by the order (lab result PDFs, imaging
// reports, dispensing receipts, etc.) and renders them inline with
// Open / Download links so the doctor can read results and update the
// encounter without leaving the editor.
//
// Also surfaces:
//   * the order's `result_summary` / `report_summary` (lab/imaging) —
//     short typed summary the lab/radiology entered alongside the file
//   * a "fulfilled" badge when the order is in a terminal positive state
//     (completed / dispensed) so the doctor can see at a glance which
//     orders are still pending vs done
//
// Backend touchpoints — no schema or endpoint changes needed:
//   GET /api/documents/?owner_model=clinical_notes.LabOrder&owner_id=N
//   GET /api/documents/?owner_model=clinical_notes.ImagingOrder&owner_id=N
//   GET /api/documents/?owner_model=clinical_notes.Prescription&owner_id=N
// ============================================================================

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { generateDocument, listDocuments, printDocument, downloadDocument } from '../../api/documents.js'
import { formatDateTime } from '../../utils/format.js'
import PostGenerationModal from './documents/PostGenerationModal.jsx'
import EmailDocumentModal from './documents/EmailDocumentModal.jsx'
import PreliminaryResultsModal from './documents/PreliminaryResultsModal.jsx'

const KIND_OPTS = {
  lab: {
    ownerModel: 'clinical_notes.LabOrder',
    emptyHint: 'No results uploaded yet.',
    label: 'Results & attachments',
    // The generator type to call when the doctor wants a branded PDF
    // of the typed result + lab info (so it can be opened, printed,
    // emailed or saved as a first-class platform document).
    docType: 'lab_result_report',
    docLabel: 'Laboratory Result Report',
  },
  imaging: {
    ownerModel: 'clinical_notes.ImagingOrder',
    emptyHint: 'No report uploaded yet.',
    label: 'Report & attachments',
    docType: 'radiology_report',
    docLabel: 'Radiology Report',
  },
  rx: {
    ownerModel: 'clinical_notes.Prescription',
    emptyHint: 'No dispensing receipt uploaded yet.',
    label: 'Fulfilment & attachments',
    // Branded "Dispensing Record" PDF — proof of dispensing for the
    // patient (insurance / employer / repeat-script verification).
    // Generated from rx.dispensing_notes + line items + pharmacy info.
    docType: 'dispensing_record',
    docLabel: 'Dispensing Record',
  },
}

/**
 * @param {object}  props
 * @param {'lab'|'imaging'|'rx'} props.kind
 * @param {object}  props.encounter           — the encounter the order belongs to (needed to generate docs)
 * @param {string|number} props.orderId       — id of the LabOrder / ImagingOrder / Prescription
 * @param {string} props.status               — current order status (drives "fulfilled" badge)
 * @param {string} [props.summary]            — result_summary / report_summary if present
 * @param {string} [props.summaryLabel]       — label for the summary card (defaults to kind-appropriate)
 * @param {Array}  [props.items]              — lab only — full LabOrderItem[] inc. nested analytes[].
 *                                              Drives the structured-results table inside View Results.
 */
export default function OrderResultsPanel({
  kind,
  encounter,
  orderId,
  status,                 // order.status — pending / completed / dispensed / etc.
  resultStatus,           // result_status — pending / preliminary / final (drives the badge)
  summary,                // result_summary / report_summary / dispensing_notes — final-state typed text
  summaryLabel,
  prelim,                 // preliminary_result_summary / preliminary_imaging_summary
  impression,             // imaging only
  comment,                // result_comment / pharmacist_comment
  ordered = [],           // [{label}] — the test/study/drug names list
  items = [],             // lab only — LabOrderItem[] (with analytes[]) for the read-only results table
  subtitle,               // override the default modal subtitle
}) {
  const opts = KIND_OPTS[kind]
  const qc = useQueryClient()
  const [postGenDoc, setPostGenDoc] = useState(null)
  const [emailDoc, setEmailDoc] = useState(null)
  const [genError, setGenError] = useState(null)

  // "View Results" surfaces the typed preliminary summary in a clean
  // focused modal so the doctor can read what the lab has said so far
  // and decide on empirical treatment. The modal is the SOLE place
  // the typed summary is displayed — keeping the panel itself a pure
  // "what files / what status" surface, no duplication.
  const [viewResultsOpen, setViewResultsOpen] = useState(false)

  const docsQ = useQuery({
    queryKey: ['order-docs', opts?.ownerModel, orderId],
    queryFn: () => listDocuments({ owner_model: opts.ownerModel, owner_id: orderId }),
    enabled: Boolean(opts && orderId),
    staleTime: 15_000,
  })

  // Generation mutation — turns the typed result/report into a branded
  // PDF that can be opened, printed, emailed or saved (it's a real
  // ClinicalDocument row tied to the encounter, just like the seven
  // documents from the encounter-header dropdown).
  const generate = useMutation({
    mutationFn: () => generateDocument({
      document_type: opts.docType,
      encounter_id: encounter?.id,
      payload: { order_id: orderId },
    }),
    onSuccess: (doc) => {
      setGenError(null)
      // Refresh the attachments list so the new PDF appears in the panel
      // immediately (it's also a ClinicalDocument owned by this order).
      qc.invalidateQueries({ queryKey: ['order-docs', opts.ownerModel, orderId] })
      qc.invalidateQueries({ queryKey: ['generated-documents', encounter?.id] })
      qc.invalidateQueries({ queryKey: ['encounter-documents', encounter?.id] })
      setPostGenDoc(doc)
    },
    onError: (err) => {
      setGenError(
        err?.response?.data?.detail
        || err?.message
        || 'Failed to generate report.'
      )
    },
  })

  if (!opts || !orderId) return null
  const docs = docsQ.data || []

  // "Fulfilled" = the lab/radiology/pharmacy says they're done with their
  // half of the workflow. Lab/imaging use 'completed'; pharmacy uses
  // 'dispensed'. Either signals the doctor can read results now.
  const isFulfilled = ['completed', 'dispensed', 'fulfilled', 'ready'].includes(
    (status || '').toLowerCase()
  )
  const hasContent = Boolean(summary) || docs.length > 0

  // Skip rendering entirely when there's nothing to show AND the order
  // isn't yet fulfilled — no point cluttering the editor with empty
  // "no results yet" panels on a routine pending order. We only show
  // the empty-state message when the order IS fulfilled but no documents
  // arrived (unusual: lab said they're done but didn't upload anything).
  if (!hasContent && !isFulfilled) return null

  // Preliminary / Final teaser — tells the doctor at a glance what's
  // available without opening the modal. Drives the panel's tone too.
  const lifecycle = (resultStatus || '').toLowerCase()
  const teaser = (() => {
    if (lifecycle === 'final') return { text: 'Final report available', tone: 'emerald' }
    if (lifecycle === 'preliminary') return { text: 'Preliminary result available', tone: 'sky' }
    if (docs.length > 0) return { text: 'Document uploaded', tone: 'emerald' }
    if (summary || prelim) return { text: 'Result entered', tone: 'sky' }
    return null
  })()

  const tone = lifecycle === 'final' || isFulfilled
    ? 'bg-emerald-50 border-emerald-200'
    : lifecycle === 'preliminary'
    ? 'bg-sky-50 border-sky-200'
    : 'bg-slate-50 border-slate-200'

  // Branded-PDF generation is reachable from the View Results modal's
  // "Generate full PDF" button. We keep the mutation here so the modal
  // can trigger it via the onGenerate callback. Hidden for Rx (no
  // generator).
  const canGenerate = Boolean(opts.docType && encounter?.id && (summary || isFulfilled))

  return (
    <>
    <div className={`mt-3 border-2 rounded-lg p-3 ${tone}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
            {opts.label}
          </div>
          {/* Lifecycle badge — Pending / Preliminary / Final */}
          {lifecycle === 'preliminary' && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-sky-600 text-white rounded-full px-2 py-0.5">
              Preliminary
            </span>
          )}
          {lifecycle === 'final' && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-600 text-white rounded-full px-2 py-0.5">
              Final
            </span>
          )}
          {!lifecycle && isFulfilled && (
            <span className="text-[10px] font-bold uppercase tracking-wide bg-emerald-600 text-white rounded-full px-2 py-0.5">
              ✓ Fulfilled
            </span>
          )}
          {teaser && (
            <span className={`text-[11px] font-medium ${teaser.tone === 'emerald' ? 'text-emerald-700' : 'text-sky-700'}`}>
              {teaser.text}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/*
            "View Results" — quick-read of the lab's typed preliminary
            summary. Always discoverable so the doctor knows where to
            look for interpretable results. Bright green when a summary
            has been entered; outlined when not (modal explains "lab
            hasn't entered one yet — refer to attached files").
            Critical for empirical-treatment decisions.
          */}
          <button
            type="button"
            onClick={() => setViewResultsOpen(true)}
            className={
              'text-[11px] font-bold rounded-full px-3 py-1 shadow-sm inline-flex items-center gap-1 ' +
              (summary
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                : 'bg-white border-2 border-slate-300 text-slate-600 hover:border-emerald-400 hover:text-emerald-700')
            }
            title={summary
              ? "Read the lab's typed preliminary summary"
              : 'Lab has not entered a preliminary summary yet'}
          >
            <span>👁️</span> View Results
          </button>
        </div>
      </div>
      {genError && (
        <div className="mb-2 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1.5 font-medium">
          {genError}
        </div>
      )}
      {generate.isPending && (
        <div className="mb-2 text-xs text-slate-500">Rendering branded PDF…</div>
      )}

      {/* Attached files */}
      {docsQ.isLoading ? (
        <div className="text-xs text-slate-500">Loading attachments…</div>
      ) : docs.length > 0 ? (
        <ul className="space-y-1.5">
          {docs.map((d) => (
            <DocRow
              key={d.id}
              doc={d}
              kind={kind}
              onEmail={(file) => setEmailDoc(file)}
            />
          ))}
        </ul>
      ) : (
        <div className="text-xs text-slate-500 italic">{opts.emptyHint}</div>
      )}
    </div>

    {/* Rich View Results modal — full status / preliminary / final /
        files surface. Pulls everything from the parent-supplied
        `order` object so the parent (LabOrderForm / ImagingOrderForm /
        PrescriptionForm) controls what's surfaced per kind. */}
    {viewResultsOpen && (
      <PreliminaryResultsModal
        kind={kind}
        title={(() => {
          if (kind === 'rx')      return `Prescription #${orderId} — Dispensing`
          if (kind === 'imaging') return `Order #${orderId} — Imaging Result`
          return `Order #${orderId} — Lab Result`
        })()}
        subtitle={subtitle}
        ordered={ordered}
        status={resultStatus || (isFulfilled ? 'final' : 'pending')}
        prelim={prelim}
        final={summary}
        impression={impression}
        comment={comment}
        files={docs}
        items={items}
        encounter={encounter}
        onClose={() => setViewResultsOpen(false)}
        onGenerate={canGenerate ? () => {
          setViewResultsOpen(false)
          generate.mutate()
        } : undefined}
      />
    )}

    {/* Post-generation modal — Open / Print / Email tiles for the
        freshly-rendered branded PDF. Reuses the same modal used by
        the encounter-header Generate Documents dropdown so the doctor
        sees a familiar action surface. */}
    {postGenDoc && (
      <PostGenerationModal
        doc={postGenDoc}
        kindLabel={opts.docLabel || 'Report'}
        onClose={() => setPostGenDoc(null)}
        onEmail={(d) => { setPostGenDoc(null); setEmailDoc(d) }}
      />
    )}
    {emailDoc && (
      <EmailDocumentModal
        doc={emailDoc}
        encounter={encounter}
        onClose={() => setEmailDoc(null)}
      />
    )}
    </>
  )
}

// File row receives `onEmail` callback so the parent can open the
// EmailDocumentModal with the file's ClinicalDocument id pre-set.
function DocRow({ doc, kind, onEmail }) {
  const filename = doc.original_filename || `Document #${doc.id}`
  const hasFile = Boolean(doc?.id)
  const size = formatBytes(doc.size_bytes)
  const uploadedBy = doc.uploaded_by_username

  // Print / Save fetch the file through the authenticated client (the gated
  // /download/ endpoint needs the JWT bearer; a plain window.open or <a
  // href> of the file_url would 404). Auto-print is best-effort — browsers
  // vary, so the tab stays open for a manual Ctrl+P.
  const handlePrint = () => { printDocument(doc).catch(() => {}) }
  const handleSave = () => { downloadDocument(doc).catch(() => {}) }

  return (
    <li className="bg-white border-2 border-slate-200 rounded-lg p-3 flex items-center gap-3 hover:border-wantok-300 transition">
      <span className="text-2xl shrink-0 leading-none">{iconFor(kind, doc)}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-slate-900 truncate">{filename}</div>
        <div className="text-[11px] text-slate-500 truncate">
          {humanKind(doc.kind)}
          {size && <> · {size}</>}
          {doc.uploaded_at && <> · {formatDateTime(doc.uploaded_at)}</>}
          {uploadedBy && <> · uploaded by {uploadedBy}</>}
        </div>
      </div>
      {hasFile && (
        <div className="flex items-center gap-1.5 shrink-0">
          <FileActionBtn icon="🖨️" label="Print" onClick={handlePrint} title="Open this file and trigger your browser's print dialog" />
          <FileActionBtn icon="✉️" label="Email" onClick={() => onEmail?.(doc)} title="Email this file as an attachment" />
          <FileActionBtn icon="💾" label="Save" onClick={handleSave} title="Save the file to your computer" />
        </div>
      )}
    </li>
  )
}

// Brand-coloured pill action — used for Print / Email / Save on each
// file row. Sized for both mouse and touch (label collapses to icon
// only below sm: breakpoint so three buttons fit on a phone).
function FileActionBtn({ icon, label, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="text-xs font-bold text-wantok-700 hover:text-white hover:bg-wantok-700 border-2 border-wantok-700 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1 transition"
    >
      <span className="text-base leading-none">{icon}</span>
      <span className="hidden sm:inline">{label}</span>
    </button>
  )
}

function FileActionLink({ icon, label, href, download, title }) {
  return (
    <a
      href={href}
      download={download}
      title={title}
      className="text-xs font-bold text-wantok-700 hover:text-white hover:bg-wantok-700 border-2 border-wantok-700 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1 no-underline transition"
    >
      <span className="text-base leading-none">{icon}</span>
      <span className="hidden sm:inline">{label}</span>
    </a>
  )
}

function iconFor(kind, doc) {
  // The MIME / kind hints are best-effort; a sensible default per surface
  // keeps the row visually consistent even when the upload metadata is sparse.
  const mime = (doc.mime_type || '').toLowerCase()
  if (mime.startsWith('image/')) return '🖼️'
  if (mime === 'application/pdf' || (doc.original_filename || '').toLowerCase().endsWith('.pdf')) return '📄'
  if (kind === 'imaging') return '🩻'
  if (kind === 'lab')     return '🧪'
  if (kind === 'rx')      return '💊'
  return '📎'
}

function humanKind(k) {
  const map = {
    lab_result:           'Lab result',
    imaging_report:       'Imaging report',
    referral_letter:      'Referral letter',
    consent_form:         'Consent',
    id_document:          'ID',
    insurance_card:       'Insurance',
    prior_record:         'Prior record',
    encounter_attachment: 'Attachment',
    sick_certificate:     'Sick certificate',
    prescription:         'Prescription PDF',
    lab_request:          'Lab request',
    imaging_request:      'Imaging request',
    invoice:              'Invoice',
    medical_report:       'Medical report',
    other:                'Document',
  }
  return map[k] || (k || 'Document').replace(/_/g, ' ')
}

function formatBytes(n) {
  if (n == null) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
